import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Collapse,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Slider,
  Autocomplete,
  IconButton,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FilterPanel } from './LMA.style';
import { FilterValues } from './ActiveFilters';
import {
  FilterConfiguration,
  FilterGroup,
  FilterField,
  CheckboxGroup,
  SliderFieldConfig,
  DateRangeFieldConfig,
  DropdownFieldConfig,
} from './types';
import ApiDropdown from './ApiDropdown';

interface DynamicFilterPanelProps {
  filterOpen: boolean;
  filters: FilterValues;
  onClearFilters: () => void;
  filterConfig?: FilterConfiguration;
  currentTabIndex?: number;
  currentTabKey?: string;
  onApplyFilters?: (filters: FilterValues) => void; // Callback with complete filter object
}

const commonElementStyle = { minWidth: 200, flex: 1 };

const DynamicFilterPanel: React.FC<DynamicFilterPanelProps> = ({
  filterOpen,
  filters,
  onClearFilters,
  filterConfig,
  currentTabIndex = 0,
  currentTabKey,
  onApplyFilters,
}) => {
  const [tempFilters, setTempFilters] = useState<FilterValues>(filters);
  const [dateRangeType, setDateRangeType] = useState<string>('');
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);

  // Sync temp state with props
  useEffect(() => {
    setTempFilters(filters);

    const hasDateFrom = filters.dateFrom && filters.dateFrom !== '';
    const hasDateTo = filters.dateTo && filters.dateTo !== '';

    if (!hasDateFrom && !hasDateTo && dateRangeType) {
      setDateRangeType('');
      setShowCustomDateRange(false);
    }
  }, [filters]);

  // Calculate active filter count
  const activeFilterCount = Object.values(filters).filter((v) => v !== '').length;

  const handleTempFilterChange = (field: string, value: string | string[]) => {
    setTempFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDateRangeChange = (value: string) => {
    setDateRangeType(value);
    setShowCustomDateRange(value === 'custom');

    if (value === 'custom') {
      handleTempFilterChange('dateFrom', '');
      handleTempFilterChange('dateTo', '');
    } else if (value !== 'custom') {
      const today = new Date();
      let startDate = '';
      let endDate = today.toISOString().split('T')[0];

      switch (value) {
        case 'today':
          startDate = endDate;
          break;
        case 'yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          startDate = yesterday.toISOString().split('T')[0];
          endDate = startDate;
          break;
        case 'last7Days':
          const last7Days = new Date(today);
          last7Days.setDate(last7Days.getDate() - 7);
          startDate = last7Days.toISOString().split('T')[0];
          endDate = today.toISOString().split('T')[0];
          break;
        case 'thisWeek':
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay());
          startDate = weekStart.toISOString().split('T')[0];
          break;
        case 'lastWeek':
          const lastWeekEnd = new Date(today);
          lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
          const lastWeekStart = new Date(lastWeekEnd);
          lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
          startDate = lastWeekStart.toISOString().split('T')[0];
          endDate = lastWeekEnd.toISOString().split('T')[0];
          break;
        case 'last30Days':
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(today.getDate() - 30);
          startDate = thirtyDaysAgo.toISOString().split('T')[0];
          break;
        default:
          startDate = '';
          endDate = '';
      }

      if (startDate) handleTempFilterChange('dateFrom', startDate);
      if (endDate) handleTempFilterChange('dateTo', endDate);
    }
  };

  const handleLeadTypeToggle = (leadType: string) => {
    const leadTypeValue = tempFilters.leadType;
    const currentLeadTypes = Array.isArray(leadTypeValue)
      ? leadTypeValue
      : (leadTypeValue as string)?.split(',').filter((v: string) => v) || [];

    const newLeadTypes = currentLeadTypes.includes(leadType)
      ? currentLeadTypes.filter((type: string) => type !== leadType)
      : [...currentLeadTypes, leadType];

    handleTempFilterChange('leadType', newLeadTypes);
  };

  const handleClearAll = () => {
    onClearFilters();
    setDateRangeType('');
    setShowCustomDateRange(false);
    setTempFilters({
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      dob: '',
      gender: '',
      address: '',
      autoMerge: '',
      manualReview: '',
      newRecord: '',
      associatedProtocols: '',
      qualifiedProtocols: '',
      protocols: '',
      userIds: '',
      site: '',
      dateFrom: '',
      dateTo: '',
      matchPercentageMin: '',
      matchPercentageMax: '',
      status: '',
      leadType: '', // Clear leadType
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_term: '',
      utm_content: '',
      sID: '',
      psecID: '',
      gclid: '',
      fbclid: '',
      atid: '',
    });
  };

  const handleApplyFilters = () => {
    // If onApplyFilters callback is provided, pass complete filter object to trigger API request
    if (onApplyFilters) {
      onApplyFilters(tempFilters);
    }
  };

  // Check if there are any age validation errors
  const hasAgeValidationError = () => {
    const minAgeValue = tempFilters['minAge'] || tempFilters['minimumAge'];
    const maxAgeValue = tempFilters['maxAge'] || tempFilters['maximumAge'];

    if (minAgeValue && maxAgeValue) {
      const minAge = parseInt(minAgeValue as string, 10);
      const maxAge = parseInt(maxAgeValue as string, 10);

      if (minAge > maxAge) {
        return true;
      }
    }

    return false;
  };

  // Render individual field based on type
  const renderField = (field: FilterField) => {
    if (!field.enabled) return null;

    switch (field.type) {
      case 'text': {
        // Fields that should only accept numbers
        const numericFields = ['minAge', 'maxAge', 'zipcode'];
        const isNumericField = numericFields.some((numField) =>
          field.id.toLowerCase().includes(numField.toLowerCase())
        );

        const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          // Allow empty string or numbers only
          if (value === '' || /^\d+$/.test(value)) {
            handleTempFilterChange(field.id, value);
          }
        };

        const handleNumericKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (
            [8, 9, 27, 13, 37, 38, 39, 40, 46].indexOf(e.keyCode) !== -1 ||
            (e.keyCode === 65 && e.ctrlKey === true) ||
            (e.keyCode === 67 && e.ctrlKey === true) ||
            (e.keyCode === 86 && e.ctrlKey === true) ||
            (e.keyCode === 88 && e.ctrlKey === true)
          ) {
            return;
          }
          if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-' || e.key === '.') {
            e.preventDefault();
          }
        };

        // Determine if this is a min or max age field
        const isMinAgeField = field.id.toLowerCase().includes('minage');
        const isMaxAgeField = field.id.toLowerCase().includes('maxage');

        // Get min/max constraints for age fields
        let minConstraint: number | undefined;
        let maxConstraint: number | undefined;
        let hasAgeError = false;
        let ageErrorMessage = '';

        if (isMinAgeField) {
          // For minimum age, max constraint is the entered max age
          const maxAgeValue = tempFilters['maxAge'] || tempFilters['maximumAge'];
          maxConstraint = maxAgeValue ? parseInt(maxAgeValue as string, 10) : undefined;

          // Validate: min age should not be greater than max age
          const currentMinAge = tempFilters[field.id]
            ? parseInt(tempFilters[field.id] as string, 10)
            : undefined;
          if (currentMinAge && maxConstraint && currentMinAge > maxConstraint) {
            hasAgeError = true;
            ageErrorMessage = 'Min age cannot be greater than max age';
          }
        } else if (isMaxAgeField) {
          // For maximum age, min constraint is the entered min age
          const minAgeValue = tempFilters['minAge'] || tempFilters['minimumAge'];
          minConstraint = minAgeValue ? parseInt(minAgeValue as string, 10) : undefined;

          // Validate: max age should not be less than min age
          const currentMaxAge = tempFilters[field.id]
            ? parseInt(tempFilters[field.id] as string, 10)
            : undefined;
          if (currentMaxAge && minConstraint && currentMaxAge < minConstraint) {
            hasAgeError = true;
            ageErrorMessage = 'Max age cannot be less than min age';
          }
        }

        return (
          <TextField
            key={field.id}
            size="small"
            label={field.label}
            value={tempFilters[field.id] || ''}
            onChange={
              isNumericField
                ? handleNumericChange
                : (e) => handleTempFilterChange(field.id, e.target.value)
            }
            onKeyDown={isNumericField ? handleNumericKeyDown : undefined}
            placeholder={field.placeholder}
            type={isNumericField ? 'number' : 'text'}
            style={commonElementStyle}
            error={hasAgeError}
            helperText={hasAgeError ? ageErrorMessage : ''}
            inputProps={
              isNumericField
                ? {
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    min: minConstraint,
                    max: maxConstraint,
                  }
                : undefined
            }
            InputProps={{
              endAdornment: tempFilters[field.id] ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => handleTempFilterChange(field.id, '')}
                    edge="end"
                    sx={{ padding: '4px' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        );
      }

      case 'dropdown': {
        const dropdownField = field as DropdownFieldConfig;

        // Use ApiDropdown component for fields with API endpoints
        if (dropdownField.apiEndpoint) {
          // For site dropdown, pass selected protocols to filter sites
          const isSiteDropdown = dropdownField.apiConfig?.responseKey === 'sites';
          const selectedProtocols = isSiteDropdown
            ? (() => {
                // Get selected protocol values from tempFilters (check both associatedProtocol and qualifiedProtocol)
                const protocolValue = tempFilters['associatedProtocols'] || '';

                // If empty string or no value, return undefined
                if (!protocolValue || protocolValue === '') {
                  return undefined;
                }

                // Convert to array if needed
                if (Array.isArray(protocolValue)) {
                  const filtered = protocolValue.filter((v) => v && v !== '');
                  return filtered.length > 0 ? filtered : undefined;
                }
                const protocols = [protocolValue as string];
                return protocols;
              })()
            : undefined;

          return (
            <ApiDropdown
              key={dropdownField.id}
              field={dropdownField}
              value={tempFilters[field.id] || (dropdownField.multiple ? [] : '')}
              onChange={(value) => handleTempFilterChange(field.id, value)}
              selectedProtocols={selectedProtocols}
              commonElementStyle={commonElementStyle}
              currentTabKey={currentTabKey}
            />
          );
        }

        // Standard dropdown without API
        const isMultiple = dropdownField.multiple ?? false;
        const isSearchable = dropdownField.searchable ?? false;

        // For multi-select, value should be an array
        // For single-select, value should be a string
        const value = isMultiple
          ? Array.isArray(tempFilters[field.id])
            ? (tempFilters[field.id] as string[])
            : ((tempFilters[field.id] as string) || '').split(',').filter((v) => v)
          : (tempFilters[field.id] as string) || '';

        // Use Autocomplete for searchable dropdowns
        if (isSearchable && isMultiple) {
          const selectedOptions = dropdownField.options.filter((opt) =>
            (value as string[]).includes(opt.value)
          );

          return (
            <Autocomplete
              key={field.id}
              multiple
              size="small"
              options={dropdownField.options}
              getOptionLabel={(option) => option.label}
              value={selectedOptions}
              onChange={(_, newValue) => {
                const selectedValues = newValue.map((opt) => opt.value);
                handleTempFilterChange(field.id, selectedValues);
              }}
              renderInput={(params) => (
                <TextField {...params} label={field.label} placeholder={field.placeholder} />
              )}
              style={commonElementStyle}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} style={{ marginRight: 8 }} />
                  {option.label}
                </li>
              )}
              disableCloseOnSelect
            />
          );
        }

        // Standard Select for non-searchable dropdowns
        return (
          <FormControl key={field.id} size="small" style={commonElementStyle}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              multiple={isMultiple}
              value={value}
              label={field.label}
              placeholder={field.label}
              onChange={(e) => {
                const selectedValue = e.target.value;
                if (isMultiple) {
                  // For multiple selection, store as array
                  handleTempFilterChange(field.id, selectedValue);
                } else {
                  handleTempFilterChange(field.id, selectedValue as string);
                }
              }}
              endAdornment={
                !isMultiple && value ? (
                  <InputAdornment position="end" sx={{ marginRight: 3 }}>
                    <IconButton
                      size="small"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        handleTempFilterChange(field.id, '');
                      }}
                      edge="end"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
              renderValue={
                isMultiple
                  ? (selected) => {
                      const selectedArray = Array.isArray(selected) ? selected : [];
                      return selectedArray
                        .map((val) => {
                          const option = dropdownField.options.find((opt) => opt.value === val);
                          return option ? option.label : val;
                        })
                        .join(', ');
                    }
                  : undefined
              }
            >
              {dropdownField.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {isMultiple && (
                    <Checkbox
                      checked={Array.isArray(value) ? value.indexOf(option.value) > -1 : false}
                    />
                  )}
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }

      case 'date':
        return (
          <TextField
            key={field.id}
            size="small"
            label={field.label}
            type="date"
            value={tempFilters[field.id] || ''}
            onChange={(e) => handleTempFilterChange(field.id, e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={commonElementStyle}
            InputProps={{
              endAdornment: tempFilters[field.id] ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => handleTempFilterChange(field.id, '')}
                    edge="end"
                    sx={{ padding: '4px', marginRight: '8px' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        );

      case 'dateRange': {
        const dateRangeField = field as DateRangeFieldConfig;
        return (
          <React.Fragment key={field.id}>
            <FormControl size="small" style={commonElementStyle}>
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={dateRangeType}
                label={field.label}
                placeholder={field.label}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                endAdornment={
                  dateRangeType ? (
                    <InputAdornment position="end" sx={{ marginRight: 3 }}>
                      <IconButton
                        size="small"
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          setDateRangeType('');
                          setShowCustomDateRange(false);
                          handleTempFilterChange('dateFrom', '');
                          handleTempFilterChange('dateTo', '');
                        }}
                        edge="end"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              >
                {dateRangeField.options?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {showCustomDateRange && (
              <>
                <TextField
                  size="small"
                  label="From Date"
                  type="date"
                  value={tempFilters.dateFrom || ''}
                  onChange={(e) => handleTempFilterChange('dateFrom', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  style={commonElementStyle}
                  inputProps={{
                    max: tempFilters.dateTo || undefined,
                  }}
                  InputProps={{
                    endAdornment: tempFilters.dateFrom ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleTempFilterChange('dateFrom', '')}
                          edge="end"
                          sx={{ padding: '4px', marginRight: '8px' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <TextField
                  size="small"
                  label="To Date"
                  type="date"
                  value={tempFilters.dateTo || ''}
                  onChange={(e) => handleTempFilterChange('dateTo', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  style={commonElementStyle}
                  inputProps={{
                    min: tempFilters.dateFrom || undefined,
                  }}
                  InputProps={{
                    endAdornment: tempFilters.dateTo ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleTempFilterChange('dateTo', '')}
                          edge="end"
                          sx={{ padding: '4px', marginRight: '8px' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </>
            )}
          </React.Fragment>
        );
      }

      case 'slider': {
        const sliderField = field as SliderFieldConfig;
        const minFieldValue = tempFilters[sliderField.fields.minField];
        const maxFieldValue = tempFilters[sliderField.fields.maxField];

        const minValue =
          parseInt(Array.isArray(minFieldValue) ? minFieldValue[0] : minFieldValue || '') ||
          sliderField.defaultValue[0];
        const maxValue =
          parseInt(Array.isArray(maxFieldValue) ? maxFieldValue[0] : maxFieldValue || '') ||
          sliderField.defaultValue[1];

        return (
          <FormControl key={field.id} size="small" sx={{ ml: 1 }} style={commonElementStyle}>
            {sliderField.showValue && (
              <span
                style={{
                  marginBottom: '-8px',
                  marginTop: '-2px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#555',
                }}
              >
                {field.label}: {minValue}-{maxValue}%
              </span>
            )}
            <Slider
              value={[minValue, maxValue]}
              onChange={(_event: Event, newValue: number | number[]) => {
                const range = newValue as number[];
                handleTempFilterChange(sliderField.fields.minField, range[0].toString());
                handleTempFilterChange(sliderField.fields.maxField, range[1].toString());
              }}
              valueLabelDisplay="auto"
              min={sliderField.min}
              max={sliderField.max}
              step={sliderField.step}
              size="small"
            />
          </FormControl>
        );
      }

      default:
        return null;
    }
  };

  // Render checkbox group
  const renderCheckboxGroup = (group: CheckboxGroup) => {
    if (!group.enabled) return null;

    // Handle lead type checkboxes (uses leadType field now)
    if (group.id === 'leadType') {
      const leadTypeValue = tempFilters.leadType;
      const currentLeadTypes = Array.isArray(leadTypeValue)
        ? leadTypeValue
        : (leadTypeValue as string)?.split(',').filter((v: string) => v) || [];

      return (
        <Box key={group.id}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              mb: 0.5,
              color: (theme: any) => theme.palette.neutral8?.main,
            }}
          >
            {group.title}
          </Typography>
          <FormGroup>
            {group.options.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={currentLeadTypes.includes(option.value)}
                    onChange={() => handleLeadTypeToggle(option.value)}
                    disableRipple
                    sx={{
                      color: option.color,
                      '&.Mui-checked': {
                        color: option.color,
                      },
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: '14px' }}>{option.label}</Typography>}
                sx={{
                  backgroundColor: currentLeadTypes.includes(option.value)
                    ? option.bgColor
                    : 'transparent',
                  borderRadius: 1,
                  p: 0,
                  mr: 0,
                  ml: 0,
                  transition: 'all 0.2s',
                  border: `1px solid ${
                    currentLeadTypes.includes(option.value)
                      ? option.borderColor || option.color
                      : 'transparent'
                  }`,
                  '& .MuiCheckbox-root': {
                    padding: '8px',
                  },
                }}
              />
            ))}
          </FormGroup>
        </Box>
      );
    }

    // Handle standard checkboxes (like dedupe status)
    const fieldName = group.field || group.id;
    return (
      <Box key={group.id}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            mb: 0.5,
            color: (theme: any) => theme.palette.neutral8?.main,
          }}
        >
          {group.title}
        </Typography>
        <FormGroup>
          {group.options.map((option) => {
            const fieldValue = tempFilters[fieldName];
            const currentValues = Array.isArray(fieldValue)
              ? fieldValue
              : (fieldValue as string)?.split(',').filter((s: string) => s) || [];
            const isChecked = currentValues.includes(option.value);

            return (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={(e) => {
                      const newValues = e.target.checked
                        ? [...currentValues, option.value]
                        : currentValues.filter((s: string) => s !== option.value);
                      handleTempFilterChange(fieldName, newValues);
                    }}
                    disableRipple
                    sx={{
                      color: option.color,
                      '&.Mui-checked': {
                        color: option.color,
                      },
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: '14px' }}>{option.label}</Typography>}
                sx={{
                  borderRadius: 1,
                  p: 0,
                  mr: 0,
                  ml: 0,
                  '& .MuiCheckbox-root': {
                    padding: '8px',
                  },
                }}
              />
            );
          })}
        </FormGroup>
      </Box>
    );
  };

  // Render filter group
  const renderFilterGroup = (group: FilterGroup) => {
    if (!group.enabled) return null;

    // Check visibility rules
    if (group.visibility?.hideOnTabs?.includes(currentTabIndex)) {
      return null;
    }

    // Render checkbox card
    if (group.type === 'checkbox-card' && group.checkboxGroups) {
      const layout = group.layout;
      return (
        <Box
          key={group.id}
          sx={{
            border: (theme: any) => `1px solid ${theme.palette.neutral3?.main || '#E9E9E9'}`,
            borderRadius: 2,
            p: 2,
            backgroundColor: (theme: any) => theme.palette.background?.paper || '#fff',
            minWidth: layout?.minWidth ? { lg: layout.minWidth } : undefined,
            maxWidth: layout?.maxWidth ? { lg: layout.maxWidth } : undefined,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gridTemplateColumns: `repeat(${
                typeof layout?.columns === 'number' ? layout.columns : 2
              }, 1fr)`,
              gap: 2,
            }}
          >
            {group.checkboxGroups
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map(renderCheckboxGroup)}
          </Box>
        </Box>
      );
    }

    // Render regular card with fields
    if (group.type === 'card' && group.fields) {
      const layout = group.layout;
      const isGridLayout = layout?.type === 'grid';
      const columns = layout?.columns;
      const isResponsiveColumns = columns && typeof columns === 'object' && 'xs' in columns;

      return (
        <Box
          key={group.id}
          sx={{
            border: (theme: any) => `1px solid ${theme.palette.neutral3?.main || '#E9E9E9'}`,
            borderRadius: 2,
            p: 1.5,
            backgroundColor: (theme: any) => theme.palette.background?.paper || '#fff',
          }}
        >
          <Box
            sx={{
              display: isGridLayout ? 'grid' : 'flex',
              gridTemplateColumns:
                isGridLayout && isResponsiveColumns
                  ? {
                      xs: `repeat(${columns.xs || 1}, 1fr)`,
                      sm: `repeat(${columns.sm || 2}, 1fr)`,
                      md: `repeat(${columns.md || 3}, 1fr)`,
                      lg: `repeat(${columns.lg || 4}, 1fr)`,
                    }
                  : undefined,
              gap: layout?.gap || 1.5,
            }}
          >
            {group.fields.sort((a, b) => a.displayOrder - b.displayOrder).map(renderField)}
          </Box>
        </Box>
      );
    }

    return null;
  };

  // If no filter config provided, render nothing
  if (!filterConfig) {
    return null;
  }

  if (!filterConfig.enabled) return null;

  // Separate checkbox cards (status filters) from regular fields
  const checkboxGroups = filterConfig.filterGroups.filter((g) => g.type === 'checkbox-card');
  const regularGroups = filterConfig.filterGroups.filter((g) => g.type === 'card');

  // Collect all fields from regular groups into a single array
  const allRegularFields = regularGroups
    .flatMap((group) => group.fields || [])
    .filter((field) => field && field.enabled)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <Collapse in={filterOpen} timeout="auto" unmountOnExit>
      <FilterPanel>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px', paddingLeft: '4px' }}>
            {filterConfig.filterLabel}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={handleClearAll}
              disabled={activeFilterCount === 0}
              sx={{
                fontSize: '12px',
                textTransform: 'none',
                color: (theme: any) => theme.palette.error?.main,
              }}
            >
              {filterConfig.clearButtonText}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApplyFilters}
              disabled={hasAgeValidationError()}
              sx={{
                fontSize: '12px',
                padding: '4px 8px',
                textTransform: 'none',
                backgroundColor: (theme: any) => theme.palette.themeColor?.main,
                color: 'white',
                '&:hover': {
                  backgroundColor: (theme: any) =>
                    theme.palette.themeColor?.dark || theme.palette.themeColor?.main,
                },
              }}
            >
              {filterConfig.applyButtonText}
            </Button>
          </Box>
        </Box>

        {/* Main Layout: Filter Fields (4 per row) and Status Card Side by Side */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', lg: 'row' },
            mt: 1,
            alignItems: 'flex-start',
          }}
        >
          {/* Left Side: All Filter Fields in Grid (4 per row) */}
          {allRegularFields.length > 0 && (
            <Box
              sx={{
                flex: 1,
                border: (theme: any) => `1px solid ${theme.palette.neutral3?.main || '#E9E9E9'}`,
                borderRadius: 2,
                p: 1.5,
                backgroundColor: (theme: any) => theme.palette.background?.paper || '#fff',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                }}
              >
                {allRegularFields.map(renderField)}
              </Box>
            </Box>
          )}

          {/* Right Side: Status Card (Checkbox Groups) */}
          {checkboxGroups.length > 0 && (
            <>
              {checkboxGroups
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map(renderFilterGroup)}
            </>
          )}
        </Box>
      </FilterPanel>
    </Collapse>
  );
};

export default DynamicFilterPanel;
